import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { Task } from "../types";
import ConfirmDialog from "./Common/ConfirmDialog";
import useAcl from "../hooks/useAcl";

interface TaskHeaderProps {
  task: Task | null;
  isEditing: boolean;
  anchorEl: HTMLElement | null;
  handleOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseMenu: () => void;
  setIsEditing: (value: boolean) => void;
  handleDelete: () => Promise<void>;
  handleCancel?: () => void;
  handleSave: () => void;
  error: string | null;
  setError: (value: string | null) => void;
  success: string | null;
  setSuccess: (value: string | null) => void;
  isLoading: boolean;
  backUrl?: string;
}

const TaskHeader: React.FC<TaskHeaderProps> = ({
  task,
  isEditing,
  anchorEl,
  handleOpenMenu,
  handleCloseMenu,
  setIsEditing,
  handleDelete,
  handleCancel,
  handleSave,
  error,
  setError,
  success,
  setSuccess,
  isLoading,
  backUrl,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { canDelete, canEdit } = useAcl("Task", task);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const parentId = queryParams.get("parentId");
  
  const getBackUrl = () => {
    if (backUrl) return backUrl;
    if (location.pathname.includes("/project/")) {
      const projectIdMatch = location.pathname.match(/\/project\/([^/]+)/);
      if (projectIdMatch) {
        return `/project/${projectIdMatch[1]}/tasks`;
      }
    }
    return parentId ? `/task/${parentId}` : "/tasks";
  };

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    try {
      await handleDelete();
      setSuccess(t("tasks.detail.deleteSuccess"));
      setTimeout(() => {
        setConfirmOpen(false);
      }, 1000);
    } catch {
      setError(t("tasks.detail.deleteError"));
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        {" "}
        <Box
          sx={{
            display: "flex",
          }}
        >
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            onClick={() => navigate(getBackUrl())}
            sx={{
              textTransform: "none",
              px: "0",
              color: "#757575",
              minWidth: "auto",
            }}
            disabled={isLoading}
          ></Button>

          <Typography
            variant="h5"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {task?.name || t("tasks.detail.title")}
          </Typography>
        </Box>
        <Box
          sx={{
            transition: "opacity 0.3s ease, transform 0.3s ease",
            opacity: isEditing ? 0 : 1,
            transform: isEditing ? "translateY(-10px)" : "translateY(0)",
            pointerEvents: isEditing ? "none" : "auto",
          }}
        >
          {!isEditing && (
            <IconButton onClick={handleOpenMenu} disabled={isLoading}>
              <MoreVertOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: isEditing ? 1 : 0,
          transform: isEditing ? "translateY(0)" : "translateY(10px)",
          pointerEvents: isEditing ? "auto" : "none",
          height: isEditing ? "auto" : 0,
          overflow: "hidden",
        }}
      >
        {isEditing && (
          <>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: "none",
                minWidth: { xs: "120px", sm: "150px" },
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              }}
              disabled={isLoading}
            >
              {t("tasks.detail.cancelButton")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                textTransform: "none",
                minWidth: { xs: "120px", sm: "150px" },
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("tasks.detail.saveButton")
              )}
            </Button>
          </>
        )}
      </Box>
      {!isEditing && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                setIsEditing(true);
                setTimeout(() => handleCloseMenu(), 0);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: "#4467A5" }} />
              </ListItemIcon>
              <ListItemText primary={t("tasks.detail.editButton")} />
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={handleOpenConfirm}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
              </ListItemIcon>
              <ListItemText primary={t("tasks.detail.deleteButton")} />
            </MenuItem>
          )}
        </Popover>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={t("tasks.detail.deleteConfirmTitle")}
        content={t("tasks.detail.deleteConfirmContent")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isLoading}
      />
    </>
  );
};

export default TaskHeader;
